import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

const declinesMotion = () => (
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  || Boolean(navigator.connection?.saveData)
);

// Muted background film. The still poster always sits underneath, so the opening
// stays complete while the film loads, or when it is missing, blocked, fails, or
// is declined (reduced motion or data saver). Visitors can always pause or play.
export default function HeroVideo({ src, poster, className = '', ...props }) {
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const userPaused = useRef(false);
  const [requested, setRequested] = useState(false);
  const [mode, setMode] = useState(src ? 'waiting' : 'failed');
  const [filmVisible, setFilmVisible] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [posterSettled, setPosterSettled] = useState(!poster);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    // A rejected promise means autoplay was blocked; the poster stays and Play is offered.
    video.play()?.catch(() => setMode((current) => (current === 'failed' ? current : 'paused')));
  }, []);

  useEffect(() => {
    if (!src || !declinesMotion()) return;
    userPaused.current = true;
    setMode('paused');
  }, [src]);

  // Request the film only after the poster has settled and the page has loaded,
  // so the first visible image never competes with the much larger film.
  useEffect(() => {
    if (!src || !posterSettled || userPaused.current) return undefined;
    const request = () => setRequested(true);
    if (document.readyState === 'complete') {
      request();
      return undefined;
    }
    window.addEventListener('load', request, { once: true });
    return () => window.removeEventListener('load', request);
  }, [src, posterSettled]);

  useEffect(() => {
    if (requested && !userPaused.current) play();
  }, [requested, play]);

  // Pause while the opening is off-screen; resume only if the visitor has not paused it.
  useEffect(() => {
    if (!requested || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) videoRef.current?.pause();
      else if (!userPaused.current) play();
    });
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [requested, play]);

  const toggle = () => {
    if (mode === 'playing') {
      userPaused.current = true;
      videoRef.current?.pause();
      setMode('paused');
      return;
    }
    userPaused.current = false;
    if (requested) play();
    else setRequested(true);
  };

  return (
    <div
      {...props}
      ref={frameRef}
      className={`dv-hero-video ${className}`}
      data-video-state={mode}
      data-film-visible={filmVisible}
    >
      {poster && !posterFailed && (
        <img
          className="dv-hero-video__poster"
          src={poster}
          alt=""
          fetchPriority="high"
          decoding="async"
          onLoad={() => setPosterSettled(true)}
          onError={() => {
            setPosterFailed(true);
            setPosterSettled(true);
          }}
        />
      )}
      {requested && mode !== 'failed' && (
        <video
          ref={videoRef}
          className="dv-hero-video__film"
          src={src}
          muted
          playsInline
          loop
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => {
            setFilmVisible(true);
            setMode('playing');
          }}
          onError={() => {
            setFilmVisible(false);
            setMode('failed');
          }}
        />
      )}
      {(mode === 'playing' || mode === 'paused') && (
        <button
          type="button"
          className="dv-hero-video__control"
          onClick={toggle}
          aria-label={mode === 'playing' ? 'Pause background video' : 'Play background video'}
        >
          {mode === 'playing' ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          <span aria-hidden="true">{mode === 'playing' ? 'Pause' : 'Play'}</span>
        </button>
      )}
    </div>
  );
}
